import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * <RotatingImage />
 *
 * Renderiza un slot con 1 o N imágenes.
 * Si hay más de 1 imagen, rota automáticamente con fade out/in cada
 * `rotationMs`. Si solo hay 1, se muestra estática sin animación.
 *
 * Props:
 * - config: { images: string[], alt: string, rotationMs?: number }
 * - className: clases del wrapper (ej: rounded, aspect ratio, etc.)
 * - imgClassName: clases del <img>
 * - dataTestId: opcional
 */
export default function RotatingImage({
  config,
  className = "",
  imgClassName = "w-full h-auto block",
  dataTestId,
  eagerLoad = false,
  aspectRatio = "16/9",
}) {
  const images = config?.images || [];
  const rotationMs = config?.rotationMs ?? 4500;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, rotationMs);
    return () => clearInterval(t);
  }, [images.length, rotationMs]);

  if (!images.length) return null;

  const current = images[index];

  return (
    <div
      className={`relative ${className}`}
      style={{ aspectRatio }}
      data-testid={dataTestId}
      data-rotating={images.length > 1 ? "true" : "false"}
    >
      <AnimatePresence mode="sync">
        <motion.img
          key={current}
          src={current}
          alt={config.alt || ""}
          initial={{ opacity: images.length > 1 ? 0 : 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className={`${imgClassName} ${
            images.length > 1 ? "absolute inset-0" : ""
          }`}
          loading={eagerLoad ? "eager" : "lazy"}
        />
      </AnimatePresence>

      {/* Spacer to preserve layout when using absolute positioning */}
      {images.length > 1 && (
        <img
          src={images[0]}
          alt=""
          aria-hidden="true"
          className={`${imgClassName} invisible`}
          loading="lazy"
        />
      )}
    </div>
  );
}
